import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronRight, ChevronDown, Filter, Zap, Target, Building } from 'lucide-react';
import { supabase } from '@/lib/supabase-client';
import { useAuth } from '../hooks/useAuth';
import { useAuthContext } from '../hooks/useAuthContext';
import { Button } from '@/components/ui/Button';
import { AddGoalModal } from '@/components/goals/AddGoalModal';
import { AddTeamGoalModal } from '@/components/goals/AddTeamGoalModal';
import { AddMyGoalModal } from '@/components/goals/AddMyGoalModal';
import { AddGoalDropdown } from '@/components/goals/AddGoalDropdown';
import { StrategyMapView } from '@/components/goals/StrategyMapView';
import { TimePeriodsModal } from '@/components/goals/TimePeriodsModal';
export function Goals() {
    const { user } = useAuth();
    const { userProfile } = useAuthContext();
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('strategy');
    const [activeView, setActiveView] = useState('map');
    const [expandedGoals, setExpandedGoals] = useState(new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [filterOwner, setFilterOwner] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [selectedPeriods, setSelectedPeriods] = useState(['All']);
    const [isTimePeriodsModalOpen, setIsTimePeriodsModalOpen] = useState(false);
    const [isEditingMission, setIsEditingMission] = useState(false);
    const [missionStatement, setMissionStatement] = useState("Add your company mission to align your work and stay inspired. Only admins can edit.");
    const [tempMissionStatement, setTempMissionStatement] = useState(missionStatement);
    const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [userTeams, setUserTeams] = useState([]);
    const [organizationName, setOrganizationName] = useState('Organization');
    const filterRef = useRef(null);
    // Fetch goals based on active tab
    useEffect(() => {
        if (!user)
            return;
        const fetchGoals = async () => {
            setLoading(true);
            try {
                let query = supabase.from('goals').select('*');
                // Filter based on active tab
                if (activeTab === 'company') {
                    // Company goals - all goals in the organization
                    if (userProfile?.organization_id) {
                        query = query.eq('organization_id', userProfile.organization_id);
                    }
                }
                else if (activeTab === 'team') {
                    // Team goals - goals assigned to user's teams
                    if (userTeams.length > 0) {
                        const teamIds = userTeams.map(t => t.id);
                        query = query.in('team_id', teamIds);
                    }
                }
                else if (activeTab === 'my') {
                    // My goals - goals owned/assigned to current user
                    query = query.eq('owner_id', user.id);
                }
                // For strategy tab, we don't fetch here (handled by StrategyMapView)
                const { data, error } = await query.order('created_at', { ascending: false });
                if (error)
                    throw error;
                setGoals(data || []);
            }
            catch (error) {
                console.error('Error fetching goals:', error);
            }
            finally {
                setLoading(false);
            }
        };
        if (activeTab !== 'strategy') {
            fetchGoals();
        }
    }, [user, activeTab, userProfile?.organization_id, userTeams]);
    // Fetch organization name and user's teams
    useEffect(() => {
        const fetchOrganizationAndTeams = async () => {
            if (!userProfile?.organization_id || !user)
                return;
            try {
                // Fetch organization name
                const { data: orgData, error: orgError } = await supabase
                    .from('organizations')
                    .select('name')
                    .eq('id', userProfile.organization_id)
                    .single();
                if (orgError)
                    throw orgError;
                if (orgData) {
                    setOrganizationName(orgData.name);
                }
                // Fetch teams that the user is a member of
                const { data, error } = await supabase
                    .from('teams')
                    .select('*')
                    .eq('organization_id', userProfile.organization_id);
                if (error)
                    throw error;
                // Filter teams where user is a member (check team_members junction table)
                const userTeamIds = [];
                for (const team of data || []) {
                    const { data: memberData } = await supabase
                        .from('team_members')
                        .select('*')
                        .eq('team_id', team.id)
                        .eq('user_id', user.id)
                        .single();
                    if (memberData) {
                        userTeamIds.push(team.id);
                    }
                }
                setUserTeams((data || []).filter(t => userTeamIds.includes(t.id)));
            }
            catch (error) {
                console.error('Error fetching organization and teams:', error);
            }
        };
        fetchOrganizationAndTeams();
    }, [user, userProfile?.organization_id]);
    // Filter goals by time periods
    const isGoalInPeriod = (goal, period) => {
        if (!goal.due_date)
            return true; // Show goals without due dates
        const dueDate = new Date(goal.due_date);
        const month = dueDate.getMonth() + 1; // 1-12
        switch (period) {
            case 'Q1': return month >= 1 && month <= 3;
            case 'Q2': return month >= 4 && month <= 6;
            case 'Q3': return month >= 7 && month <= 9;
            case 'Q4': return month >= 10 && month <= 12;
            case 'H1': return month >= 1 && month <= 6;
            case 'H2': return month >= 7 && month <= 12;
            case 'All': return true;
            default: return true;
        }
    };
    const filterGoalsByPeriods = (goals) => {
        if (selectedPeriods.includes('All') || selectedPeriods.length === 0) {
            return goals;
        }
        return goals.filter(goal => selectedPeriods.some(period => isGoalInPeriod(goal, period)));
    };
    // Build hierarchical goal structure
    const buildGoalHierarchy = (goals) => {
        const goalMap = new Map();
        const rootGoals = [];
        goals.forEach(goal => {
            goalMap.set(goal.id, { ...goal, children: [], subGoalsCount: 0 });
        });
        goals.forEach(goal => {
            const goalWithChildren = goalMap.get(goal.id);
            if (goal.parent_id && goalMap.has(goal.parent_id)) {
                const parent = goalMap.get(goal.parent_id);
                if (!parent.children)
                    parent.children = [];
                parent.children.push(goalWithChildren);
                parent.subGoalsCount = (parent.subGoalsCount || 0) + 1;
            }
            else {
                rootGoals.push(goalWithChildren);
            }
        });
        return rootGoals;
    };
    const filteredGoals = filterGoalsByPeriods(goals);
    const hierarchicalGoals = buildGoalHierarchy(filteredGoals);
    const toggleGoal = (goalId) => {
        setExpandedGoals(prev => {
            const newSet = new Set(prev);
            if (newSet.has(goalId)) {
                newSet.delete(goalId);
            }
            else {
                newSet.add(goalId);
            }
            return newSet;
        });
    };
    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'On track';
            case 'at_risk': return 'At risk';
            case 'completed': return 'Completed';
            case 'paused': return 'Paused';
            default: return 'No status';
        }
    };
    const getStatusDotColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-500';
            case 'at_risk': return 'bg-red-500';
            case 'completed': return 'bg-blue-500';
            case 'paused': return 'bg-yellow-500';
            default: return 'bg-gray-400';
        }
    };
    const formatDateRange = (start, end) => {
        if (!start || !end)
            return '';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const startStr = startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        const endStr = endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        return `${startStr} - ${endStr}`;
    };
    const handleEditMission = () => {
        setTempMissionStatement(missionStatement);
        setIsEditingMission(true);
    };
    const handleSaveMission = () => {
        setMissionStatement(tempMissionStatement);
        setIsEditingMission(false);
    };
    const handleCancelMission = () => {
        setTempMissionStatement(missionStatement);
        setIsEditingMission(false);
    };
    const handleCreateBlankGoal = () => {
        setSelectedTemplate(null);
        setIsAddGoalModalOpen(true);
    };
    const handleCreateFromTemplate = (template) => {
        setSelectedTemplate(template);
        setIsAddGoalModalOpen(true);
    };
    const handleSaveGoal = async (goalData) => {
        if (!user || !userProfile?.organization_id)
            return;
        try {
            const newGoal = {
                user_id: user.id,
                organization_id: userProfile.organization_id,
                name: goalData.name,
                description: goalData.description || '',
                type: goalData.type,
                status: goalData.status,
                target_value: 100, // Default to 100 for percentage
                current_value: goalData.currentValue || 0,
                unit: goalData.measurement || 'percent',
                owner_id: goalData.ownerId || user.id,
                priority: goalData.priority,
                tags: goalData.tags || [],
                linked_tasks: [],
                linked_projects: [],
                progress: goalData.progress || 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            const { data, error } = await supabase
                .from('goals')
                .insert([newGoal])
                .select()
                .single();
            if (error)
                throw error;
            if (data) {
                setGoals(prev => [data, ...prev]);
                // Reset form states
                setSelectedTemplate(null);
            }
        }
        catch (error) {
            console.error('Error saving goal:', error);
        }
    };
    const handleApplyFilters = () => {
        setShowFilters(false);
    };
    const handleClearFilters = () => {
        setFilterOwner('');
        setFilterStatus('');
        setShowFilters(false);
    };
    const handleApplyTimePeriods = (periods) => {
        setSelectedPeriods(periods);
    };
    const getTimePeriodLabel = () => {
        if (selectedPeriods.includes('All') || selectedPeriods.length === 0) {
            return 'All';
        }
        if (selectedPeriods.length === 1) {
            return selectedPeriods[0];
        }
        return `${selectedPeriods.length} periods`;
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilters(false);
            }
        };
        if (showFilters) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilters]);
    const renderGoalCard = (goal) => {
        const hasSubGoals = (goal.subGoalsCount ?? 0) > 0;
        const isExpanded = expandedGoals.has(goal.id);
        return (_jsxs("div", { className: "border-b border-gray-200 dark:border-gray-700 last:border-b-0", children: [goal.targetValue !== undefined && (_jsx("div", { className: "h-2 bg-gray-100 dark:bg-gray-800", children: _jsx("div", { className: "h-full bg-green-500 transition-all duration-300", style: { width: `${goal.progress}%` } }) })), _jsxs("div", { className: "p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50", children: [hasSubGoals && (_jsx("div", { className: "mb-2", children: _jsxs("button", { onClick: () => toggleGoal(goal.id), className: "flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200", children: [isExpanded ? _jsx(ChevronDown, { className: "w-4 h-4" }) : _jsx(ChevronRight, { className: "w-4 h-4" }), goal.subGoalsCount, " sub-goals"] }) })), _jsx("h3", { className: "text-base font-medium text-gray-900 dark:text-white mb-3", children: goal.name }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: `w-2 h-2 rounded-full ${getStatusDotColor(goal.status)}` }), _jsx("span", { className: "text-sm text-gray-700 dark:text-gray-300", children: getStatusText(goal.status) })] }), goal.targetValue !== undefined && (_jsxs("div", { className: "flex items-center gap-1", children: [_jsxs("span", { className: "text-sm text-gray-700 dark:text-gray-300", children: [goal.progress, "%"] }), _jsx(Zap, { className: "w-3 h-3 text-yellow-500" })] }))] }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("span", { className: "text-sm text-gray-600 dark:text-gray-400", children: goal.dueDate ? 'H2 FY22' : '' }), goal.startDate && goal.dueDate && (_jsx("span", { className: "text-sm text-gray-600 dark:text-gray-400", children: formatDateRange(goal.startDate, goal.dueDate) })), _jsx("div", { className: "w-6 h-6 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center", children: _jsx("span", { className: "text-[10px] text-white font-medium", children: "JD" }) })] })] })] })] }, goal.id));
    };
    return (_jsxs("div", { className: "flex flex-col h-full bg-white dark:bg-gray-900", children: [_jsxs("div", { className: "flex-shrink-0 border-b border-gray-200 dark:border-gray-800", children: [_jsxs("div", { className: "flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-800", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center", children: _jsx(Building, { className: "w-5 h-5 text-white" }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("h1", { className: "text-lg font-semibold text-gray-900 dark:text-white", children: organizationName }), _jsx(ChevronDown, { className: "w-4 h-4 text-gray-400" })] })] }), _jsx("div", { className: "flex items-center gap-4", children: _jsxs("button", { className: "flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200", children: [_jsx("div", { className: "w-4 h-4 rounded-full border-2 border-current flex items-center justify-center", children: _jsx("span", { className: "text-xs", children: "i" }) }), "Who can see goals?"] }) })] }), _jsx("div", { className: "px-6 py-3", children: _jsxs("div", { className: "flex items-center gap-6", children: [_jsx("button", { onClick: () => { setActiveTab('strategy'); setActiveView('map'); }, className: `pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'strategy'
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`, children: "Strategy map" }), _jsx("button", { onClick: () => { setActiveTab('company'); setActiveView('list'); }, className: `pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'company'
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`, children: "Company goals" }), _jsx("button", { onClick: () => { setActiveTab('team'); setActiveView('list'); }, className: `pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'team'
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`, children: "Team goals" }), _jsx("button", { onClick: () => { setActiveTab('my'); setActiveView('list'); }, className: `pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'my'
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`, children: "My goals" })] }) })] }), activeTab === 'strategy' ? (_jsx(StrategyMapView, {})) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex-shrink-0 px-6 py-6 border-b border-gray-200 dark:border-gray-800", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900 dark:text-white mb-2", children: "Mission" }), isEditingMission ? (_jsxs("div", { className: "space-y-3", children: [_jsx("textarea", { value: tempMissionStatement, onChange: (e) => setTempMissionStatement(e.target.value), className: "w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none", rows: 2, autoFocus: true }), _jsxs("div", { className: "flex items-center gap-2 justify-end", children: [_jsx("button", { onClick: handleCancelMission, className: "px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors", children: "Cancel" }), _jsx("button", { onClick: handleSaveMission, className: "px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors", children: "Save" })] })] })) : (_jsx("p", { onClick: handleEditMission, className: "text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200", children: missionStatement }))] }), _jsx("div", { className: "flex-shrink-0 px-6 py-4 bg-gray-50 dark:bg-gray-800/30", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "flex items-center gap-3", children: _jsx(AddGoalDropdown, { onCreateBlankGoal: handleCreateBlankGoal, onCreateFromTemplate: handleCreateFromTemplate }) }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("button", { className: "text-sm text-blue-600 dark:text-blue-400 hover:underline", children: "Send feedback" }), _jsxs("div", { className: "relative", ref: filterRef, children: [_jsxs("button", { onClick: () => setShowFilters(!showFilters), className: "flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-gray-600", children: [_jsx(Filter, { className: "w-4 h-4" }), "Filter: Open goals"] }), showFilters && (_jsx("div", { className: "absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50", children: _jsxs("div", { className: "p-4 space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Owner" }), _jsx("input", { type: "text", value: filterOwner, onChange: (e) => setFilterOwner(e.target.value), placeholder: "Search or select goal owner", className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Status" }), _jsx("input", { type: "text", value: filterStatus, onChange: (e) => setFilterStatus(e.target.value), placeholder: "Search or select status", className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700" })] }), _jsxs("div", { className: "flex items-center justify-end gap-2 pt-2", children: [_jsx("button", { onClick: handleClearFilters, className: "px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors", children: "Cancel" }), _jsx("button", { onClick: handleApplyFilters, className: "px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg transition-colors", children: "Apply filters" })] })] }) }))] }), _jsxs("button", { onClick: () => setIsTimePeriodsModalOpen(true), className: "flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-gray-600", children: ["Time Periods: ", getTimePeriodLabel(), _jsx(ChevronDown, { className: "w-4 h-4" })] })] })] }) }), _jsx("div", { className: "flex-1 overflow-y-auto", children: loading ? (_jsx("div", { className: "flex items-center justify-center py-12", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" }), _jsx("p", { className: "text-gray-500 dark:text-gray-400", children: "Loading goals..." })] }) })) : hierarchicalGoals.length > 0 ? (_jsx("div", { className: "divide-y divide-gray-200 dark:divide-gray-700", children: hierarchicalGoals.map((goal) => renderGoalCard(goal)) })) : (_jsxs("div", { className: "flex flex-col items-center justify-center py-12", children: [_jsx("div", { className: "w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4", children: _jsx(Target, { className: "w-8 h-8 text-blue-600 dark:text-blue-400" }) }), _jsx("h3", { className: "text-lg font-semibold text-gray-900 dark:text-white mb-2", children: "No goals yet" }), _jsx("p", { className: "text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md", children: "Start tracking your objectives and key results to achieve your goals." }), _jsxs(Button, { variant: "primary", onClick: () => setIsAddGoalModalOpen(true), children: [_jsx(Plus, { className: "h-4 w-4 mr-2" }), "Create First Goal"] })] })) })] })), activeTab !== 'strategy' && (_jsxs(_Fragment, { children: [activeTab === 'company' && (_jsx(AddGoalModal, { isOpen: isAddGoalModalOpen, onClose: () => setIsAddGoalModalOpen(false), onSave: handleSaveGoal, template: selectedTemplate })), activeTab === 'team' && (_jsx(AddTeamGoalModal, { isOpen: isAddGoalModalOpen, onClose: () => setIsAddGoalModalOpen(false), onSave: handleSaveGoal, template: selectedTemplate })), activeTab === 'my' && (_jsx(AddMyGoalModal, { isOpen: isAddGoalModalOpen, onClose: () => setIsAddGoalModalOpen(false), onSave: handleSaveGoal, template: selectedTemplate }))] })), _jsx(TimePeriodsModal, { isOpen: isTimePeriodsModalOpen, onClose: () => setIsTimePeriodsModalOpen(false), selectedPeriods: selectedPeriods, onApply: handleApplyTimePeriods })] }));
}
